import Vue from 'vue';
import store from '@/store/store';

const PAYMENTS_PATH = '/payments';
const PAYMENTS_UPGRADE_PATH = '/payments-upgrade';
const DASHBOARD_PATH = '/dashboard';
const FEATURE_FLAGS_TIMEOUT = 5000;
const TOAST_TIMEOUT = 5000;

/**
 * Shows an error toast message
 * @param {string} messageKey - Translation key for the error message
 */
const showErrorToast = (messageKey) => {
  const fToast = window.angular?.element(document.body).injector()?.get('fToast');

  if (fToast) {
    const message = Vue.prototype.$t ? Vue.prototype.$t(messageKey) : messageKey;
    fToast.showToastMessage({
      type: 'attention',
      message,
      timeout: TOAST_TIMEOUT
    });
  }
};

/**
 * Determines the appropriate redirect path based on the source route
 * @param {Object} obj - Object containing route properties
 * @param {string} obj.path - The route path
 * @returns {string} - The redirect path as a string
 */
const getRedirectPath = ({path = DASHBOARD_PATH}) => {
  const isRootPath = path === '/';
  const isPaymentsPath = path.includes(PAYMENTS_PATH);
  const shouldRedirectToDashboard = isRootPath || isPaymentsPath;
  
  return shouldRedirectToDashboard ? DASHBOARD_PATH : path;
};

/**
 * Checks if this is a dialog opened from Angular (BusinessStore not initialized).
 */
const isAngularDialog = () => {
  return !store.state?.BusinessStore;
};

const areFeatureFlagsLoaded = () => store.getters['BusinessStore/featureFlagsLoaded'];

const getPaymentPermissions = () => ({
  hasPaymentsModule: store.getters['BusinessStore/hasFeature']('payments_module'),
  hasPaymentsPromote: store.getters['BusinessStore/hasFeature']('pkg.payments.promote')
});

/**
 * Handles payment permissions with promotion upgrade logic.
 */
const handlePaymentPermissions = (next, from) => {
  const { hasPaymentsModule, hasPaymentsPromote } = getPaymentPermissions();

  if (hasPaymentsModule) {
    return next();
  }

  if (hasPaymentsPromote) {
    return next(PAYMENTS_UPGRADE_PATH);
  }

  showErrorToast('common.errors.insufficient_package');
  return next(getRedirectPath(from));
};

/**
 * Handles upgrade permissions (simpler logic)
 */
const handleUpgradePermissions = (next, from) => {
  const { hasPaymentsModule, hasPaymentsPromote } = getPaymentPermissions();

  if (hasPaymentsModule || hasPaymentsPromote) {
    return next();
  }

  showErrorToast('common.errors.insufficient_package');
  return next(getRedirectPath(from));
};

const createFeatureFlagsPromise = () => {
  if (areFeatureFlagsLoaded()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const unwatch = store.watch(
      (state, getters) => getters['BusinessStore/featureFlagsLoaded'],
      (loaded) => {
        if (loaded) {
          unwatch();
          resolve();
        }
      }
    );
  });
};

const createTimeoutPromise = () => new Promise((_, reject) => {
  setTimeout(reject, FEATURE_FLAGS_TIMEOUT);
});

const waitForFeatureFlags = async (next, from, permissionHandler) => {
  try {
    await Promise.race([
      createFeatureFlagsPromise(),
      createTimeoutPromise()
    ]);
    permissionHandler(next, from);
  } catch {
    showErrorToast('common.errors.insufficient_package');
    next(getRedirectPath(from));
  }
};

/**
 * Route guard for payment-related routes that require the payments_module feature flag
 */
export const paymentsModuleGuard = (to, from, next) => {
  if (isAngularDialog()) {
    return next();
  }

  return (areFeatureFlagsLoaded()
    ? handlePaymentPermissions(next, from)
    : waitForFeatureFlags(next, from, handlePaymentPermissions));
};

/**
 * Route guard for payment upgrade routes.
 */
export const paymentsUpgradeGuard = (to, from, next) => {
  if (isAngularDialog()) {
    return next();
  }

  return (areFeatureFlagsLoaded()
    ? handleUpgradePermissions(next, from)
    : waitForFeatureFlags(next, from, handleUpgradePermissions));
};
